import { UnstyledButton, useMantineTheme } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";

const BackNavigationButton = ({ label = "Back", fallbackTo, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();

  const resolveParentRoute = (pathname) => {
    const segments = pathname.split("/").filter(Boolean);

    if (segments.length <= 1) {
      return "/";
    }

    if (segments[0] === "operator" && segments[1] === "operators") {
      if (segments.length === 2 || segments[2] === "create") {
        return "/dashboard";
      }
      return "/operator/operators";
    }

    if (segments[0] === "user" && segments[1] === "users") {
      if (segments.length === 2 || segments[2] === "create") {
        return "/user/users";
      }
      return "/user/users";
    }

    if (segments[0] === "issuance") {
      if (segments[1] === "all-policy" && segments[2] === "trip") {
        return "/issuance/all-policy";
      }
      if (segments[1] === "all-travelers" && segments.length >= 3) {
        return "/issuance/all-travelers";
      }
      if (segments[1] === "policies" && segments[2] === "trip") {
        return "/issuance/user-specific-policy";
      }
      if (segments[1] === "unissued-policies") {
        return "/issuance/unissued-policies";
      }
      if (segments[1] === "quote-links") {
        return "/issuance/quote-links";
      }
    }

    if (segments[0] === "sales" && segments[1] === "leads") {
      return "/sales/leads";
    }

    if (segments[0] === "cover-api") {
      const moduleSegment = segments[1];
      const dynamicPlanActions = ["benefits", "pricing"];
      const listModules = [
        "adventures",
        "benefits",
        "plan-approval",
        "plan-reports",
        "mother-covers",
        "master-covers",
        "customize-covers",
        "mother-covers-view",
        "master-covers-view",
        "customize-covers-view",
        "explore-plans",
      ];

      // /cover-api/:planType/:id/(benefits|pricing)[/edit]
      if (
        segments.length >= 4 &&
        dynamicPlanActions.includes(segments[3]) &&
        moduleSegment
      ) {
        return `/cover-api/${moduleSegment}`;
      }

      // /cover-api/:module/(child...)
      if (moduleSegment && listModules.includes(moduleSegment)) {
        // If already on module list page, go to dashboard (plan parent).
        if (segments.length === 2) {
          return "/dashboard";
        }
        // Otherwise go to module list page.
        return `/cover-api/${moduleSegment}`;
      }

      // Fallback inside cover-api: route to safe authorized parent.
      return "/dashboard";
    }

    const adventuresIndex = segments.indexOf("adventures");
    if (adventuresIndex > -1 && segments.length > adventuresIndex + 1) {
      return `/${segments.slice(0, adventuresIndex + 1).join("/")}`;
    }

    const coverRoots = ["mother-covers", "master-covers", "customize-covers"];
    const coverRootIndex = segments.findIndex((segment) =>
      coverRoots.includes(segment)
    );

    if (coverRootIndex > -1 && segments.length > coverRootIndex + 1) {
      return `/${segments.slice(0, coverRootIndex + 1).join("/")}`;
    }

    const parentSegments = segments.slice(0, -1);
    return `/${parentSegments.join("/")}`;
  };

  const handleBack = () => {
    const returnTo = location.state?.returnTo;
    if (typeof returnTo === "string" && returnTo.startsWith("/")) {
      navigate(returnTo);
      return;
    }

    const listPath = location.state?.listPath;
    const listSearch = location.state?.listSearch || "";
    if (typeof listPath === "string" && listPath.startsWith("/")) {
      navigate(`${listPath}${listSearch}`);
      return;
    }

    const routeTarget = fallbackTo || resolveParentRoute(location.pathname);

    if (routeTarget && routeTarget !== location.pathname) {
      navigate(`${routeTarget}${listSearch}`);
      return;
    }

    // Keep deterministic route hierarchy; avoid browser-history based redirects.
    navigate("/dashboard");
  };

  return (
    <UnstyledButton
      onClick={handleBack}
      aria-label={label}
      styles={{
        root: {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.colors.brand?.[9] || theme.colors.blue[7],
          padding: 0,
          lineHeight: 1,
        },
      }}
      {...props}
    >
      <IconArrowLeft size={20} stroke={2.2} />
    </UnstyledButton>
  );
};

export default BackNavigationButton;